import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FC, useContext, useEffect, useMemo, useState } from 'react'

import { formatDate } from 'src/utils/dateUtils'

import { faLongArrowAltRight } from '@fortawesome/free-solid-svg-icons'

import InfoBlock from './InfoBlock'

import 'src/sass/Note.scss'
import { AuthContext, useTranslator } from 'ufinet-web-functions'
import { useFindActivitiesQuery } from 'src/modules/incidents/application/IncidentActivityQueries'
import { HttpIncidentRepository } from 'src/modules/incidents/infrastructure/HttpIncidentRepository'
import { toast } from 'react-toastify'
import { IncidentDetailDTO } from 'src/modules/incidents/application/dto/IncidentDetailDto'


type NoteProps = {
	activityId: string
	date: string
	from: string
	to: string
	subject: string
	content: string
	createdBy: string
	isExternal: boolean
	isFirstNote: boolean
	isLastNote: boolean
	attachmentsFile: AttachmentDTO[] | null
}

type AttachmentDTO = {
	fileName: string
	documentBody: string
	mimetype: string
	filesize: number
}

type FetchedNoteDetails = {
  subject: string
  description: string
  from: string
  to: string
  ownerName: string
  createOn: string
  attachmentsFile?: {
    fileName: string | null
    documentBody: string
    mimeType: string | null
    fileSize: number
  }[] | null
}

const Note: FC<NoteProps> = ({
	date,
	from,
	to,
	subject,
	activityId,
	content,
}) => {
	const authData = useContext(AuthContext)
	const [isExpanded, setIsExpanded] = useState(false)
	const [fullNote, setFullNote] = useState<FetchedNoteDetails | null>(null)
	const [isFetching, setIsFetching] = useState(false);
	const incidentRepository = useMemo(() => HttpIncidentRepository(authData), [authData])
	const translate = useTranslator()

	const { refetch: findActivity, isLoading } = useFindActivitiesQuery(
		incidentRepository,
		{ activityId },
	)

	const generateDownloadLink = (fileName: string, documentBody: string, mimetype: string): string => {
		const byteCharacters = atob(documentBody)
		const byteNumbers = new Uint8Array(byteCharacters.length)

		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i)
		}

		const blob = new Blob([byteNumbers], { type: mimetype })
		return URL.createObjectURL(blob)
	}

	const toggleAccordion = async () => {
		if (!isExpanded && !fullNote) {
			try {
				setIsFetching(true);
				const { data, isError } = await findActivity();
				if (isError || !data) throw new Error('Error al cargar detalles');
				setFullNote(data);
			} catch (error) {
				console.error('Error al obtener datos:', error);
				toast.error('Ocurrió un error al obtener la información.');
			} finally {
				setIsFetching(false);
			}
		}
		setIsExpanded(prev => !prev);
	};

	const hasAttachment = Array.isArray(fullNote?.attachmentsFile) && fullNote.attachmentsFile.length > 0
	const hasImage = Array.isArray(fullNote?.attachmentsFile) && fullNote?.attachmentsFile.some(f => f?.documentBody && f.mimeType?.startsWith('image/'))

	return (
		<div className="note-container">
			<div className="note-header">
				<span className="note-date">{formatDate(date, '/', true, false, true)}</span>
				<div className="note-from-to">
					<span>{from ? from : 'Ticketing Ufinet'}</span>
						<FontAwesomeIcon icon={faLongArrowAltRight} className="note-arrow" />
					<span>{to}</span>
				</div>
			</div>
			<div className="note-subject">{subject}</div>
			<div className="note-description">
				<InfoBlock label="Descripción" value={content} />
			</div>

			<button className="note-button" onClick={toggleAccordion} disabled={isFetching}>
				{isExpanded 
					? 'Ocultar'
					: isFetching
						? translate("TICKETS.CHARGED")
						: translate("TICKETS.ATTACHMENT.VIEW")}
			</button>

			{isExpanded && fullNote && (
				<div className='mt-6 mb-0'>
					<label className='info-label'>{translate("TICKETS.ATTACHMENT")}</label>
					<div className={`note-content-grid ${!hasAttachment ? 'single-column' : ''}`}>
						{hasAttachment ? (
								<div className="note-files">
									{fullNote.attachmentsFile!
										.map((file, idx) => {
										const url = generateDownloadLink(file.fileName ?? `archivo_${idx}`, file.documentBody, file.mimeType!)

										return (
											<div key={idx} className="note-file">
												<span className="note-file-icon">📄</span>
												<a 
													href={url} 
													target="_blank"
													rel="noopener noreferrer"
													className="note-file-name"
												>
													{file.fileName ?? translate("TICKETS.ATTACHMENT.FILE")}
												</a>
												<a
													href={url} 
													target="_blank"
													rel="noopener noreferrer"
													className="note-download-icon"
													title={translate("TICKETS.ATTACHMENT.DOWNLOAD")}
												>
													⬇️
												</a>
											</div>
										)
										})}
								</div>
							)
						:
						(
							<div>
								<p>{translate("TICKETS.ATTACHMENT.NO")}</p>
							</div>
						)}

						<div></div>
						<div className="note-footer">
							{fullNote?.ownerName}
						</div>

					</div>
				</div>
			)}
			
		</div>
	)
	
}

export { Note }